import { Product } from '@components/ui/Product';
import { formatLineBatchDate } from '@shared/utils/formatLineBatchDate';
import { sanitizeBatchNumberForDisplay } from '@shared/utils/sanitizeBatchDisplay';
import React from 'react';
import { LinePick, PreparationLineWithProduct } from '../../models';
import type { UniqueBatchOption } from '../../utils/linePicksForOrder';
import { uniqueBatchesFromPicks } from '../../utils/linePicksForOrder';

function packingPickRowShowsLotBadge(b: UniqueBatchOption): boolean {
  return !!sanitizeBatchNumberForDisplay(b.batch_number) || Boolean(b.expiry_date);
}

function PackingLineBatchOptList({ batchOpts }: { batchOpts: UniqueBatchOption[] }) {
  return (
    <div className="mt-2 space-y-1">
      <p className="text-xs font-medium text-muted-foreground">Lotes pickeados</p>
      <ul className="flex flex-wrap gap-1.5">
        {batchOpts.map((b) => {
          const lot = sanitizeBatchNumberForDisplay(b.batch_number);
          return (
            <li key={b.batch_id} className="text-xs bg-muted/60 border border-border rounded-md px-2 py-1">
              {lot ? <span className="font-medium text-foreground">{lot}</span> : null}
              {b.expiry_date ? (
                <span className="text-muted-foreground">
                  {lot ? ' ' : null}· Cad. {formatLineBatchDate(b.expiry_date)}
                </span>
              ) : null}
              <span className="text-muted-foreground"> · {b.pickedQty} uds.</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface ProductsListProps {
  lines: PreparationLineWithProduct[];
  orders?: any[];
  isPreparationCompleted?: boolean;
  title?: string;
  preparationId?: number; // Para acceder al localStorage si es necesario
  /** Lotes pickeados (GET /preparations/:id → line_picks) */
  linePicks?: LinePick[];
}

export const ProductsList: React.FC<ProductsListProps> = ({
  lines,
  orders = [],
  isPreparationCompleted = false,
  linePicks = [],
}) => {
  // Función auxiliar para construir URL completa de imagen (como en Packing original)
  const buildImageUrl = (imageHash: string) => {
    if (!imageHash) return '';
    if (imageHash.startsWith('http')) return imageHash;
    return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageHash}`;
  };

  // Función para calcular si una línea está completada (como en Packing original - ProductForm.tsx)
  const calculateLineCompletion = (line: PreparationLineWithProduct) => {
    if (isPreparationCompleted) {
      return {
        processedQuantity: line.qty,
        isCompleted: true,
      };
    }

    // Cantidad completada proveniente SOLO de pedidos terminados (con processed_at)
    // NO usar localStorage aquí - solo pedidos realmente completados
    const completedQuantityFromFinishedOrders = orders.reduce((total, order) => {
      if (!order.shipment?.processed_at) return total; // Solo pedidos terminados

      let orderCompletedQty = 0;
      (order.lines || []).forEach((orderLine) => {
        if (orderLine.pack && orderLine.pack.lines) {
          // Es un pack - multiplicar cantidad del producto × cantidad de packs
          const packQuantity = orderLine.qty || 1;
          orderLine.pack.lines.forEach((packLine) => {
            if (packLine.product?.code === line.product?.code) {
              orderCompletedQty += (packLine.qty || 0) * packQuantity;
            }
          });
        } else if (orderLine.product && orderLine.product.code === line.product?.code) {
          // Es un producto individual
          orderCompletedQty += orderLine.qty || 0;
        }
      });

      return total + orderCompletedQty;
    }, 0);

    // Procesado = SOLO pedidos completados (sin localStorage)
    const processed = Math.min(completedQuantityFromFinishedOrders, line.qty);

    return {
      processedQuantity: processed,
      isCompleted: processed >= line.qty,
    };
  };

  if (!lines || lines.length === 0) {
    return (
      <div className="bg-background border border-border rounded-lg p-6">
        <div className="text-center">
          <div className="text-muted-foreground mb-2">
            <svg
              className="mx-auto h-12 w-12 text-muted-foreground/50"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">No hay productos</h3>
          <p className="text-sm text-muted-foreground">Esta preparación no tiene productos asignados.</p>
        </div>
      </div>
    );
  }

  // Separar productos completados y pendientes usando la nueva lógica
  const linesWithCompletion = lines.map((line) => ({
    ...line,
    ...calculateLineCompletion(line),
  }));

  const completedLines = linesWithCompletion.filter((line) => line.isCompleted);
  const pendingLines = linesWithCompletion.filter((line) => !line.isCompleted);

  return (
    <div className="flex flex-col space-y-4 h-full">
      {/* Productos pendientes */}
      {pendingLines.length > 0 && (
        <div className="flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-3 flex-shrink-0">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="text-orange-600 dark:text-orange-400">Productos pendientes</span>
              <span className="text-sm text-muted-foreground">({pendingLines.length})</span>
            </h3>
          </div>

          <div className="space-y-3">
            {pendingLines.map((line) => {
              const remaining = line.qty - line.processedQuantity;
              const progressPercentage = (line.processedQuantity / line.qty) * 100;
              const picksForLine = linePicks.filter(
                (p) => p.product_id === line.product_id && p.stock_location_order_line_id === line.id,
              );
              const batchOpts = uniqueBatchesFromPicks(picksForLine);

              return (
                <div key={line.id} className="bg-background border border-border rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <Product
                        id={line.product_id}
                        src={buildImageUrl(line.product?.image_hash || '')}
                        name={line.product?.name || `Producto ID: ${line.product_id}`}
                        code={line.product?.code}
                        acronym={line.product?.name?.charAt(0).toUpperCase()}
                        showImageModal={true}
                        enableLineClamp={true}
                      />
                      {batchOpts.some((b) => packingPickRowShowsLotBadge(b)) ? (
                        <PackingLineBatchOptList batchOpts={batchOpts.filter((b) => packingPickRowShowsLotBadge(b))} />
                      ) : null}
                    </div>

                    {/* Información de cantidades */}
                    <div className="flex flex-col items-end space-y-1 ml-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-semibold text-orange-600 dark:text-orange-400">
                          {line.processedQuantity}
                        </span>
                        <span className="text-muted-foreground">/</span>
                        <span className="text-lg font-semibold text-muted-foreground">{line.qty}</span>
                      </div>

                      <div className="text-xs text-muted-foreground">{remaining} pendientes</div>

                      {/* Barra de progreso */}
                      <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-orange-500 transition-all duration-300"
                          style={{ width: `${progressPercentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Productos completados */}
      {completedLines.length > 0 && (
        <div className="flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-3 flex-shrink-0">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="text-green-600 dark:text-green-400">Productos completados</span>
              <span className="text-sm text-muted-foreground">({completedLines.length})</span>
            </h3>
          </div>

          <div className="space-y-3 overflow-y-auto max-h-[40vh]">
            {completedLines.map((line) => {
              const picksForLine = linePicks.filter(
                (p) => p.product_id === line.product_id && p.stock_location_order_line_id === line.id,
              );
              const batchOpts = uniqueBatchesFromPicks(picksForLine);

              return (
                <div
                  key={line.id}
                  className="bg-green-50/30 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <Product
                        id={line.product_id}
                        src={buildImageUrl(line.product?.image_hash || '')}
                        name={line.product?.name || `Producto ID: ${line.product_id}`}
                        code={line.product?.code}
                        acronym={line.product?.name?.charAt(0).toUpperCase()}
                        showImageModal={true}
                        enableLineClamp={true}
                      />
                      {batchOpts.some((b) => packingPickRowShowsLotBadge(b)) ? (
                        <PackingLineBatchOptList batchOpts={batchOpts.filter((b) => packingPickRowShowsLotBadge(b))} />
                      ) : null}
                    </div>

                    {/* Información de cantidades completadas */}
                    <div className="flex flex-col items-end space-y-1 ml-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                          {line.processedQuantity}
                        </span>
                        <span className="text-muted-foreground">/</span>
                        <span className="text-lg font-semibold text-muted-foreground">{line.qty}</span>
                      </div>

                      <div className="text-xs text-green-600 dark:text-green-400 font-medium">✓ Completado</div>

                      {/* Barra de progreso completada */}
                      <div className="w-16 h-2 bg-green-200 dark:bg-green-800 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500" style={{ width: '100%' }} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
